import { Fragment, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";

export interface ConfirmationModalConfig {
  title: string;
  description: React.ReactNode | string;
  buttonText: string;
  buttonTextCancel?: string;
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onApprove: (params?: { [x: string]: any }) => void | Promise<void>;
  params?: object;
  hideCancelButton?: boolean;
  isLoading?: boolean;
  error?: string | null;
}

const Spinner = () => (
  <svg
    className="animate-spin h-4 w-4"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export function ConfirmationModal({
  isOpen,
  title,
  description,
  buttonText,
  buttonTextCancel = "Cancel",
  onApprove,
  setOpen,
  hideCancelButton = false,
  isLoading = false,
  error = null,
}: ConfirmationModalConfig) {
  const cancelButtonRef = useRef(null);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[100]"
        initialFocus={cancelButtonRef}
        onClose={isLoading ? () => {} : setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0 confirmation-modal">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-tablePrimary p-6 text-left shadow-xl transition-all sm:my-8 w-fit max-w-md">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-semibold text-primary text-center"
                >
                  {title}
                </Dialog.Title>
                <div className="mt-2 text-sm text-secondary text-center">
                  {typeof description === "string" ? (
                    <p>{description}</p>
                  ) : (
                    description
                  )}
                </div>
                {error && (
                  <div className="mt-3 p-2 bg-red-100 border border-red-300 rounded text-red-700 text-sm text-center">
                    {error}
                  </div>
                )}
                <div className="mt-6 flex justify-center gap-3">
                  {!hideCancelButton && (
                    <button
                      ref={cancelButtonRef}
                      onClick={() => setOpen(false)}
                      disabled={isLoading}
                      className="w-[50%] rounded border border-primaryBlue bg-white px-4 py-2 text-sm font-medium text-primaryBlue hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {buttonTextCancel}
                    </button>
                  )}
                  <button
                    onClick={() => onApprove()}
                    disabled={isLoading}
                    className="w-[50%] rounded bg-primaryBlue px-4 py-2 text-sm font-medium text-white hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isLoading && <Spinner />}
                    {buttonText}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default ConfirmationModal;
